import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SEO } from "@/components/SEO";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Loader2, BarChart3, Users, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";

interface SurveyResponse {
  id: string;
  member_name: string;
  principle: string;
  funding_source: string;
  funding_source_other: string | null;
  seed_sector_eligibility: string;
  eligibility_inspection: boolean;
  eligibility_lab_test: boolean;
  eligibility_distance_req: boolean;
  eligibility_other: boolean;
  eligibility_other_text: string | null;
  parallel_mechanism: string;
  spo_position: string;
  additional_comments: string | null;
  created_at: string;
}

interface ChartData {
  name: string;
  value: number;
  percentage: string;
}

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00', '#ff00ff'];

const AdminSurveyResults = () => {
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalResponses, setTotalResponses] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    const fetchSurveyResults = async () => {
      try {
        const { data, error } = await supabase
          .from('survey_responses')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching survey results:', error);
          return;
        }

        setResponses(data || []);
        setTotalResponses(data?.length || 0);
      } catch (err) {
        console.error('Exception fetching survey results:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSurveyResults();
  }, []);

  const calculatePercentages = (field: keyof SurveyResponse): ChartData[] => {
    if (totalResponses === 0) return [];

    const counts: { [key: string]: number } = {};
    
    responses.forEach(response => {
      const value = response[field];
      const key = value?.toString() || 'No Response';
      counts[key] = (counts[key] || 0) + 1;
    });

    return Object.entries(counts).map(([name, value]) => ({
      name,
      value,
      percentage: ((value / totalResponses) * 100).toFixed(1)
    }));
  };

  const calculateBooleanPercentages = (field: keyof SurveyResponse): ChartData[] => {
    if (totalResponses === 0) return [];

    const yesCount = responses.filter(r => r[field] === true).length;
    const noCount = totalResponses - yesCount;

    return [
      {
        name: 'Yes',
        value: yesCount,
        percentage: ((yesCount / totalResponses) * 100).toFixed(1)
      },
      {
        name: 'No',
        value: noCount,
        percentage: ((noCount / totalResponses) * 100).toFixed(1)
      }
    ];
  };

  const downloadReport = () => {
    const headers = [
      'ID',
      'Member Name',
      'Principle',
      'Funding Source',
      'Funding Source Other',
      'Seed Sector Eligibility',
      'Eligibility Inspection',
      'Eligibility Lab Test',
      'Eligibility Distance Req',
      'Eligibility Other',
      'Eligibility Other Text',
      'Parallel Mechanism',
      'SPO Position',
      'Additional Comments',
      'Created At'
    ];

    const csvData = responses.map(response => [
      response.id,
      response.member_name,
      response.principle,
      response.funding_source,
      response.funding_source_other || '',
      response.seed_sector_eligibility,
      response.eligibility_inspection ? 'Yes' : 'No',
      response.eligibility_lab_test ? 'Yes' : 'No',
      response.eligibility_distance_req ? 'Yes' : 'No',
      response.eligibility_other ? 'Yes' : 'No',
      response.eligibility_other_text || '',
      response.parallel_mechanism,
      response.spo_position,
      response.additional_comments || '',
      new Date(response.created_at).toLocaleString()
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `survey-results-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPDFReport = async () => {
    try {
      toast({
        title: "Generating Report",
        description: "Opening print-optimized report in new window...",
      });

      const { data, error } = await supabase.functions.invoke('generate-survey-report');
      
      if (error) {
        console.error('Error generating report:', error);
        toast({
          title: "Error",
          description: "Failed to generate report. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Open the HTML in a new window for printing
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(data);
        newWindow.document.close();
        
        toast({
          title: "Success!",
          description: "Report opened in new window. Print dialog will appear automatically.",
        });
      } else {
        toast({
          title: "Popup Blocked",
          description: "Please allow popups and try again.",
          variant: "destructive",
        });
      }

    } catch (error) {
      console.error('Exception generating report:', error);
      toast({
        title: "Error",
        description: "Failed to generate report. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const principleData = calculatePercentages('principle');
  const fundingSourceData = calculatePercentages('funding_source');
  const seedSectorEligibilityData = calculatePercentages('seed_sector_eligibility');
  const parallelMechanismData = calculatePercentages('parallel_mechanism');
  const spoPositionData = calculatePercentages('spo_position');
  
  const inspectionData = calculateBooleanPercentages('eligibility_inspection');
  const labTestData = calculateBooleanPercentages('eligibility_lab_test');
  const distanceReqData = calculateBooleanPercentages('eligibility_distance_req');
  const otherEligibilityData = calculateBooleanPercentages('eligibility_other');

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Survey Results | Admin Dashboard" 
        description="View and analyze survey response data and statistics"
      />
      
      <div className="container mx-auto py-8 px-4 pt-24">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Survey Results</h1>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Download Report
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={downloadReport} className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Download CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={downloadPDFReport} className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Download PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-primary">{totalResponses}</p>
              <p className="text-muted-foreground">Total Survey Responses</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Principle */}
          <Card>
            <CardHeader>
              <CardTitle>Principle Support</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={principleData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {principleData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Funding Source */}
          <Card>
            <CardHeader>
              <CardTitle>Funding Source</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={fundingSourceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [`${value} (${fundingSourceData.find(d => d.value === value)?.percentage}%)`, 'Responses']} />
                  <Bar dataKey="value" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Seed Sector Eligibility */}
          <Card>
            <CardHeader>
              <CardTitle>Seed Sector Eligibility</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={seedSectorEligibilityData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {seedSectorEligibilityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* SPO Position */}
          <Card>
            <CardHeader>
              <CardTitle>SPO Position</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={spoPositionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [`${value} (${spoPositionData.find(d => d.value === value)?.percentage}%)`, 'Responses']} />
                  <Bar dataKey="value" fill="#ffc658" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Parallel Mechanism */}
          <Card>
            <CardHeader>
              <CardTitle>Parallel Mechanism</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={parallelMechanismData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {parallelMechanismData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Eligibility Requirements */}
          <Card>
            <CardHeader>
              <CardTitle>Eligibility Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium mb-2">Inspection Required</h4>
                  <div className="flex gap-4">
                    {inspectionData.map((item, index) => (
                      <div key={item.name} className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded" 
                          style={{ backgroundColor: COLORS[index] }}
                        />
                        <span>{item.name}: {item.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Lab Test Required</h4>
                  <div className="flex gap-4">
                    {labTestData.map((item, index) => (
                      <div key={item.name} className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded" 
                          style={{ backgroundColor: COLORS[index] }}
                        />
                        <span>{item.name}: {item.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Distance Requirement</h4>
                  <div className="flex gap-4">
                    {distanceReqData.map((item, index) => (
                      <div key={item.name} className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded" 
                          style={{ backgroundColor: COLORS[index] }}
                        />
                        <span>{item.name}: {item.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Other Eligibility</h4>
                  <div className="flex gap-4">
                    {otherEligibilityData.map((item, index) => (
                      <div key={item.name} className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded" 
                          style={{ backgroundColor: COLORS[index] }}
                        />
                        <span>{item.name}: {item.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminSurveyResults;